import { Vector3 } from 'three';

import { Skeleton } from '../../skeleton/core';
import {
  NON_THUMB_FINGER_ROLE_NAMES,
  isFingerHelperLikeName,
  isMetacarpalLikeName,
} from '../../schema/namingCatalog';
import type { SkeletonSpatialData } from '../spatial';
import { findFingerChains } from '../spatial';
import type { RecognizerConfig } from '../config';
import { FINGER_PATTERNS } from '../config';

// ─── Chain helpers (inlined from chains module) ──────────────────────────

function extractPathsToLeaves(
  skel: Skeleton,
  startIdx: number,
  ignoreAux = true,
): number[][] {
  const paths: number[][] = [];

  function getEffectiveChildren(idx: number): number[] {
    const bone = skel.bone(idx);
    const result: number[] = [];
    for (const c of bone.children) {
      const child = skel.bone(c);
      if (ignoreAux && (child.auxiliary || isFingerHelperLikeName(child.name))) {
        result.push(...getEffectiveChildren(c));
      } else {
        result.push(c);
      }
    }
    return result;
  }

  function dfs(idx: number, current: number[]): void {
    const bone = skel.bone(idx);
    const skipCurrent = ignoreAux && (bone.auxiliary || isFingerHelperLikeName(bone.name));
    if (!skipCurrent) current.push(idx);
    const children = getEffectiveChildren(idx);
    if (children.length === 0) {
      if (!skipCurrent && current.length > 0) paths.push([...current]);
    } else {
      for (const child of children) dfs(child, current);
    }
    if (!skipCurrent) current.pop();
  }

  dfs(startIdx, []);
  return paths;
}

// ─── Constants ──────────────────────────────────────────────────────────

const FINGER_DIGIT_MAP_0_BASED: Record<number, string> = {
  0: 'Thumb',
  1: 'Index',
  2: 'Middle',
  3: 'Ring',
  4: 'Pinky',
};

const FINGER_DIGIT_MAP_1_BASED: Record<number, string> = {
  1: 'Thumb',
  2: 'Index',
  3: 'Middle',
  4: 'Ring',
  5: 'Pinky',
};

const FINGER_LETTER_MAP: Record<string, number> = {
  a: 0,
  b: 1,
  c: 2,
  d: 3,
  e: 4,
};

const ORDERED_NON_THUMB_NAMES = [...NON_THUMB_FINGER_ROLE_NAMES];
const GENERIC_FINGER_TOKENS = ['finger', 'doigt'];

function effectiveFingerChildren(skel: Skeleton, parentIdx: number): number[] {
  const result: number[] = [];
  for (const childIdx of skel.bone(parentIdx).children) {
    const child = skel.bone(childIdx);
    if (child.auxiliary || isFingerHelperLikeName(child.name)) {
      result.push(...effectiveFingerChildren(skel, childIdx));
    } else {
      result.push(childIdx);
    }
  }
  return result;
}

function nonThumbFingerNamesForAvailableCount(count: number): string[] {
  if (count <= 0) return [];
  if (count === 1) return ['Index'];
  if (count === 2) return ['Index', 'Pinky'];
  if (count === 3) return ['Index', 'Middle', 'Pinky'];
  return [...ORDERED_NON_THUMB_NAMES];
}

// ─── Regex patterns ─────────────────────────────────────────────────────

const _RE_FINGER_DIGIT = /(?:finger|doigt)[_\-.]?([0-9]+)(?![0-9])/i;
const _RE_FINGER_LETTER = /(?:finger|doigt)[_\-.]?([a-e])(?![a-z])/i;
const _RE_INDEX_COUNTER = /index[_\-.]?0*[1-9][0-9]*$/i;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function _fingerTypeFromName(nameLower: string): string | null {
  for (const [fingerName, patterns] of Object.entries(FINGER_PATTERNS)) {
    if (fingerName === 'Thumb') continue;
    if (patterns.some((pattern) => nameLower.includes(pattern))) {
      return fingerName;
    }
  }
  return null;
}

function _fingerSegmentNumber(nameLower: string, fingerType: string): number | null {
  const patterns = FINGER_PATTERNS[fingerType] ?? [];
  for (const pattern of patterns) {
    const match = new RegExp(`${escapeRegExp(pattern)}[_\\-.]?(\\d+)`, 'i').exec(nameLower);
    if (match) return parseInt(match[1], 10);
  }
  return null;
}

function _hasExportJointSuffix(nameLower: string): boolean {
  return /(?:_env|_jnt|-env|-jnt)$/u.test(nameLower);
}

function _pathHasFingerNaming(skel: Skeleton, path: number[]): boolean {
  return path.some((boneIdx) => {
    const nameLower = skel.bone(boneIdx).name.toLowerCase();
    return (
      _fingerTypeFromName(nameLower) !== null ||
      GENERIC_FINGER_TOKENS.some((token) => nameLower.includes(token))
    );
  });
}

function _hasImmediateFingerNaming(nameLower: string): boolean {
  return (
    Object.values(FINGER_PATTERNS).some((patterns) =>
      patterns.some((pattern) => nameLower.includes(pattern)),
    ) ||
    GENERIC_FINGER_TOKENS.some((token) => nameLower.includes(token)) ||
    _RE_FINGER_DIGIT.test(nameLower) ||
    _RE_FINGER_LETTER.test(nameLower)
  );
}

function _pathStartIndexForFingerNaming(
  skel: Skeleton,
  path: number[],
): number {
  if (path.length < 2) return 0;
  if (_isMetacarpalLikeRoot(skel, path[0])) return 1;

  const rootName = skel.bone(path[0]).name.toLowerCase();
  const nextName = skel.bone(path[1]).name.toLowerCase();
  const rootIsTransparentPalmHub =
    rootName.includes('palm') &&
    !_hasImmediateFingerNaming(rootName) &&
    _hasImmediateFingerNaming(nextName);

  return rootIsTransparentPalmHub ? 1 : 0;
}

// ─── Helper: build a finger chain from a path ───────────────────────────

function _chainFromPath(
  skel: Skeleton,
  path: number[],
  maxPhalanges = 3,
): number[] {
  let start = 0;
  if (
    _isMetacarpalLikeRoot(skel, path[0]) &&
    path.length > 1
  ) {
    start = 1;
  }
  const end = start + maxPhalanges;
  return path.slice(start, end);
}

function _shouldTreatGenericRootAsMetacarpal(path: number[]): boolean {
  return path.length >= 4;
}

function _chainFromGenericPath(
  path: number[],
  isThumb: boolean,
  splitGenericMetacarpal = false,
): number[] {
  const start =
    !isThumb && splitGenericMetacarpal && _shouldTreatGenericRootAsMetacarpal(path)
      ? 1
      : 0;
  return path.slice(start, start + 3);
}

function _metacarpalFromGenericPath(
  path: number[],
  isThumb: boolean,
  splitGenericMetacarpal = false,
): number[] | null {
  if (
    isThumb ||
    !splitGenericMetacarpal ||
    !_shouldTreatGenericRootAsMetacarpal(path)
  ) {
    return null;
  }
  return [path[0]];
}

function _isIndexCounterMetacarpalRoot(skel: Skeleton, boneIdx: number): boolean {
  const bone = skel.bone(boneIdx);
  const nameLower = bone.name.toLowerCase();
  if (!_RE_INDEX_COUNTER.test(nameLower)) return false;
  if (bone.children.length === 0) return false;
  for (const childIdx of bone.children) {
    const child = skel.bone(childIdx);
    if (child.auxiliary) continue;
    if (GENERIC_FINGER_TOKENS.some((tok) => child.name.toLowerCase().includes(tok))) return true;
  }
  return false;
}

function _isImplicitNumberedMetacarpalPath(skel: Skeleton, path: number[]): boolean {
  if (path.length < 4) return false;
  const firstName = skel.bone(path[0]).name.toLowerCase();
  const fingerType = _fingerTypeFromName(firstName);
  if (fingerType === null) return false;
  if (!_hasExportJointSuffix(firstName)) return false;

  const firstSegment = _fingerSegmentNumber(firstName, fingerType);
  if (firstSegment !== 1) return false;

  for (let i = 1; i < 4; i += 1) {
    const name = skel.bone(path[i]).name.toLowerCase();
    if (_fingerTypeFromName(name) !== fingerType) return false;
    if (_fingerSegmentNumber(name, fingerType) !== firstSegment + i) return false;
  }

  return true;
}

function _isImplicitNumberedMetacarpalRoot(skel: Skeleton, boneIdx: number): boolean {
  return extractPathsToLeaves(skel, boneIdx, true).some((path) =>
    _isImplicitNumberedMetacarpalPath(skel, path),
  );
}

function _isZeroBasedNonThumbMetacarpalPath(
  skel: Skeleton,
  path: number[],
): boolean {
  if (path.length < 4) return false;

  const firstName = skel.bone(path[0]).name.toLowerCase();
  const fingerType = _fingerTypeFromName(firstName);
  if (fingerType === null || fingerType === 'Thumb') return false;

  const firstSegment = _fingerSegmentNumber(firstName, fingerType);
  if (firstSegment !== 0) return false;

  for (let i = 1; i < 4; i += 1) {
    const name = skel.bone(path[i]).name.toLowerCase();
    if (_fingerTypeFromName(name) !== fingerType) return false;
    if (_fingerSegmentNumber(name, fingerType) !== i) return false;
  }

  return true;
}

function _isZeroBasedNonThumbMetacarpalRoot(
  skel: Skeleton,
  boneIdx: number,
): boolean {
  return extractPathsToLeaves(skel, boneIdx, true).some((path) =>
    _isZeroBasedNonThumbMetacarpalPath(skel, path),
  );
}

function _isMetacarpalLikeRoot(skel: Skeleton, boneIdx: number): boolean {
  const nameLower = skel.bone(boneIdx).name.toLowerCase();
  if (isMetacarpalLikeName(nameLower)) return true;
  return (
    _isIndexCounterMetacarpalRoot(skel, boneIdx) ||
    _isImplicitNumberedMetacarpalRoot(skel, boneIdx) ||
    _isZeroBasedNonThumbMetacarpalRoot(skel, boneIdx)
  );
}

// ─── Helper: spatial thumb identification ───────────────────────────────

function _identifyThumbSpatially(
  skel: Skeleton,
  roots: number[],
  handPos: Vector3,
  minDeviation = 0.3,
  pathsByRoot: Map<number, number[]> | null = null,
): number | null {
  if (roots.length < 3) return null;

  const allDirs: Vector3[] = [];

  for (const r of roots) {
    const e = skel.bone(r).restWorld.elements;
    const v = new Vector3(e[12] - handPos.x, e[13] - handPos.y, e[14] - handPos.z);
    const n = v.length();
    allDirs.push(n > 1e-8 ? v.clone().divideScalar(n) : v.clone());
  }

  let bestRoot: number | null = null;
  let bestDev = 0.0;
  const deviations = new Map<number, number>();

  for (let i = 0; i < roots.length; i++) {
    const avgOther = new Vector3();
    let count = 0;
    for (let j = 0; j < allDirs.length; j++) {
      if (j !== i) {
        avgOther.add(allDirs[j]);
        count++;
      }
    }
    if (count > 0) avgOther.divideScalar(count);
    const avgNorm = avgOther.length();
    if (avgNorm < 1e-8) continue;
    avgOther.divideScalar(avgNorm);
    const dev = 1.0 - allDirs[i].dot(avgOther);
    deviations.set(roots[i], dev);
    if (dev > bestDev) {
      bestDev = dev;
      bestRoot = roots[i];
    }
  }

  if (pathsByRoot !== null && roots.length === 3) {
    const distances = roots
      .map((root) => {
        const e = skel.bone(root).restWorld.elements;
        const dist = new Vector3(e[12] - handPos.x, e[13] - handPos.y, e[14] - handPos.z).length();
        return [root, dist] as const;
      })
      .sort((a, b) => a[1] - b[1]);

    const [closestRoot, closestDistance] = distances[0];
    const [, middleDistance] = distances[1];
    const closestDev = deviations.get(closestRoot) ?? 0;
    if (
      closestDistance < middleDistance * 0.75 &&
      closestDev >= minDeviation * 0.4
    ) {
      return closestRoot;
    }
  }

  if (pathsByRoot !== null && roots.length >= 4) {
    const lengths = roots
      .map((root) => [root, pathsByRoot.get(root)?.length ?? 0] as const)
      .filter(([, length]) => length > 0);
    if (lengths.length === 0) return bestDev >= minDeviation ? bestRoot : null;

    const shortestLength = Math.min(...lengths.map(([, length]) => length));
    const shortestRoots = lengths.filter(([, length]) => length === shortestLength);
    const sortedLengths = lengths.map(([, length]) => length).sort((a, b) => a - b);
    const medianLength = sortedLengths[Math.floor(sortedLengths.length / 2)];

    if (shortestRoots.length === 1 && shortestLength < medianLength) {
      const [shortestRoot] = shortestRoots[0];
      const shortestDev = deviations.get(shortestRoot) ?? 0;
      if (shortestDev >= minDeviation * 0.65 && bestDev - shortestDev <= 0.4) {
        return shortestRoot;
      }
    }
  }

  return bestDev >= minDeviation ? bestRoot : null;
}

// ─── Helper: order non-thumb finger roots spatially ─────────────────────

function _orderFingerRootsSpatially(
  skel: Skeleton,
  roots: number[],
  handIdx: number,
  side: string,
  thumbPos: Vector3 | null = null,
): number[] {
  if (roots.length <= 1) return [...roots];

  const handE = skel.bone(handIdx).restWorld.elements;
  const handPos = new Vector3(handE[12], handE[13], handE[14]);

  const positions: Vector3[] = roots.map(r => {
    const e = skel.bone(r).restWorld.elements;
    return new Vector3(e[12], e[13], e[14]);
  });

  const centroid = new Vector3();
  for (const p of positions) centroid.add(p);
  centroid.divideScalar(positions.length);

  const centered = positions.map(p => p.clone().sub(centroid));

  // PCA via covariance
  let c00 = 0, c01 = 0, c02 = 0, c11 = 0, c12 = 0, c22 = 0;
  for (const c of centered) {
    c00 += c.x * c.x; c01 += c.x * c.y; c02 += c.x * c.z;
    c11 += c.y * c.y; c12 += c.y * c.z; c22 += c.z * c.z;
  }

  const { values, vectors } = eigenSymmetric3x3(c00, c01, c02, c11, c12, c22);

  const order = [0, 1, 2].sort((a, b) => values[b] - values[a]);
  let spreadAxis = vectors[order[0]];

  let oriented = false;

  if (thumbPos !== null) {
    const thumbProj = new Vector3().subVectors(thumbPos, centroid).dot(spreadAxis);
    if (thumbProj < 0) spreadAxis = spreadAxis.clone().negate();
    oriented = true;
  }

  if (!oriented) {
    const hand = skel.bone(handIdx);
    const parentIdx = hand.parent;
    if (parentIdx !== null) {
      const parentE = skel.bone(parentIdx).restWorld.elements;
      const parentPos = new Vector3(parentE[12], parentE[13], parentE[14]);
      const armDir = new Vector3().subVectors(handPos, parentPos);
      const armNorm = armDir.length();
      if (armNorm > 1e-8) armDir.divideScalar(armNorm);

      const avgFingerDir = new Vector3().subVectors(centroid, handPos);
      const avgNorm = avgFingerDir.length();
      if (avgNorm > 1e-8) avgFingerDir.divideScalar(avgNorm);

      const lateral = new Vector3().crossVectors(armDir, avgFingerDir);
      const latNorm = lateral.length();
      if (latNorm > 1e-8) {
        lateral.divideScalar(latNorm);
        if (side === 'Left') {
          if (spreadAxis.dot(lateral) < 0) spreadAxis = spreadAxis.clone().negate();
        } else {
          if (spreadAxis.dot(lateral) > 0) spreadAxis = spreadAxis.clone().negate();
        }
      }
    }
  }

  const projections: [number, number][] = roots.map(r => {
    const e = skel.bone(r).restWorld.elements;
    const pos = new Vector3(e[12], e[13], e[14]);
    return [r, pos.sub(centroid).dot(spreadAxis)];
  });
  projections.sort((a, b) => b[1] - a[1]);

  return projections.map(([r]) => r);
}

function _assignGenericFingerPaths(
  skel: Skeleton,
  side: string,
  pathsByRoot: Map<number, number[]>,
  orderedRoots: number[],
  thumbRoot: number | null,
  evidencePrefix: string,
): [Record<string, number[]>, Record<string, string>] {
  const result: Record<string, number[]> = {};
  const evidence: Record<string, string> = {};
  const remainingRoots = orderedRoots.filter((root) => root !== thumbRoot);
  const splitGenericMetacarpals = thumbRoot !== null && remainingRoots.length < 4;

  if (thumbRoot !== null) {
    const thumbPath = pathsByRoot.get(thumbRoot);
    if (thumbPath) {
      const thumbName = `${side}Thumb`;
      result[thumbName] = _chainFromGenericPath(thumbPath, true);
      evidence[thumbName] = `${evidencePrefix} (spatial thumb)`;
    }
  }

  const fingerNames = nonThumbFingerNamesForAvailableCount(remainingRoots.length);
  for (let i = 0; i < remainingRoots.length; i += 1) {
    const root = remainingRoots[i];
    const path = pathsByRoot.get(root);
    if (!path) continue;

    const fingerName = fingerNames[i] ?? `Finger${i + 1}`;
    const baseName = `${side}${fingerName}`;
    const metacarpal = _metacarpalFromGenericPath(path, false, splitGenericMetacarpals);
    if (metacarpal) {
      const metacarpalName = `${baseName}Metacarpal`;
      result[metacarpalName] = metacarpal;
      evidence[metacarpalName] = `${evidencePrefix} (generic metacarpal split)`;
    }

    result[baseName] = _chainFromGenericPath(path, false, splitGenericMetacarpals);
    evidence[baseName] = `${evidencePrefix} (${fingerName}, spatial order)`;
  }

  return [result, evidence];
}

// ─── Eigen decomposition (Jacobi) for 3x3 symmetric matrix ─────────────

function eigenSymmetric3x3(
  m00: number, m01: number, m02: number,
  m11: number, m12: number, m22: number,
): { values: number[]; vectors: Vector3[] } {
  const a = [
    [m00, m01, m02],
    [m01, m11, m12],
    [m02, m12, m22],
  ];
  const v = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];

  for (let iter = 0; iter < 50; iter++) {
    let maxVal = 0;
    let p = 0;
    let q = 1;
    for (let i = 0; i < 3; i++) {
      for (let j = i + 1; j < 3; j++) {
        if (Math.abs(a[i][j]) > maxVal) {
          maxVal = Math.abs(a[i][j]);
          p = i;
          q = j;
        }
      }
    }
    if (maxVal < 1e-12) break;

    const app = a[p][p];
    const aqq = a[q][q];
    const apq = a[p][q];

    const theta =
      Math.abs(app - aqq) < 1e-15
        ? Math.PI / 4
        : 0.5 * Math.atan2(2 * apq, app - aqq);

    const c = Math.cos(theta);
    const s = Math.sin(theta);

    const newA: number[][] = a.map(row => [...row]);
    newA[p][p] = c * c * app + 2 * s * c * apq + s * s * aqq;
    newA[q][q] = s * s * app - 2 * s * c * apq + c * c * aqq;
    newA[p][q] = 0;
    newA[q][p] = 0;

    for (let r = 0; r < 3; r++) {
      if (r === p || r === q) continue;
      const arp = a[r][p];
      const arq = a[r][q];
      newA[r][p] = c * arp + s * arq;
      newA[p][r] = newA[r][p];
      newA[r][q] = -s * arp + c * arq;
      newA[q][r] = newA[r][q];
    }

    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        a[i][j] = newA[i][j];
      }
    }

    for (let r = 0; r < 3; r++) {
      const vp = v[r][p];
      const vq = v[r][q];
      v[r][p] = c * vp + s * vq;
      v[r][q] = -s * vp + c * vq;
    }
  }

  return {
    values: [a[0][0], a[1][1], a[2][2]],
    vectors: [
      new Vector3(v[0][0], v[1][0], v[2][0]),
      new Vector3(v[0][1], v[1][1], v[2][1]),
      new Vector3(v[0][2], v[1][2], v[2][2]),
    ],
  };
}

// ─── Spatial-only detection ─────────────────────────────────────────────

function _detectFingersSpatial(
  skel: Skeleton,
  handIdx: number,
  side: string,
  spatialData: SkeletonSpatialData,
): Record<string, number[]> {
  const rawFingers = findFingerChains(skel, handIdx, spatialData);

  if (Object.keys(rawFingers).length === 0) return {};

  const pathsByRoot = new Map<number, number[]>();
  for (const chain of Object.values(rawFingers)) {
    if (chain.length === 0) continue;
    pathsByRoot.set(chain[0], chain);
  }

  const handE = skel.bone(handIdx).restWorld.elements;
  const handPos = new Vector3(handE[12], handE[13], handE[14]);
  const roots = [...pathsByRoot.keys()];
  const thumbRoot = _identifyThumbSpatially(skel, roots, handPos, 0.3, pathsByRoot);

  let thumbPosHint: Vector3 | null = null;
  if (thumbRoot !== null) {
    const thumbE = skel.bone(thumbRoot).restWorld.elements;
    thumbPosHint = new Vector3(thumbE[12], thumbE[13], thumbE[14]);
  }

  const orderedRoots = [
    ...(thumbRoot !== null ? [thumbRoot] : []),
    ..._orderFingerRootsSpatially(
      skel,
      roots.filter((root) => root !== thumbRoot),
      handIdx,
      side,
      thumbPosHint,
    ),
  ];

  const [result] = _assignGenericFingerPaths(
    skel,
    side,
    pathsByRoot,
    orderedRoots,
    thumbRoot,
    'Fingers: spatial analysis',
  );
  return result;
}

// ─── detectFingers ──────────────────────────────────────────────────────

export function detectFingers(
  skel: Skeleton,
  handIdx: number,
  side: string,
  config: RecognizerConfig,
  spatialData: SkeletonSpatialData | null = null,
  useSpatialFallback = false,
): [Record<string, number[]>, Record<string, string>] {
  const hand = skel.bone(handIdx);
  const fingerEvidence: Record<string, string> = {};

  const handChildren = effectiveFingerChildren(skel, handIdx);

  // PHASE 0: SPATIAL FALLBACK
  if (useSpatialFallback && spatialData !== null) {
    const spatialFingers = _detectFingersSpatial(skel, handIdx, side, spatialData);
    if (spatialFingers && Object.keys(spatialFingers).length >= 3) {
      const evidence: Record<string, string> = {};
      for (const name of Object.keys(spatialFingers)) {
        evidence[name] = 'Fingers: spatial analysis (generic naming)';
      }
      return [spatialFingers, evidence];
    }
  }

  const fingers: Record<string, number[]> = {};

  const fingerPaths: number[][] = [];
  for (const child of handChildren) {
    fingerPaths.push(...extractPathsToLeaves(skel, child, true));
  }

  // Detect metacarpals
  for (const child of handChildren) {
    const bone = skel.bone(child);
    const nameLower = bone.name.toLowerCase();
    if (_isMetacarpalLikeRoot(skel, child)) {
      let fingerType: string | null = null;
      for (const [fname, patterns] of Object.entries(FINGER_PATTERNS)) {
        if (patterns.some(pat => nameLower.includes(pat))) {
          fingerType = fname;
          break;
        }
      }
      if (fingerType && fingerType !== 'Thumb') {
        fingers[`${side}${fingerType}Metacarpal`] = [child];
      }
    }
  }

  // PHASE 1: EXPLICIT NAMING
  for (const path of fingerPaths) {
    if (path.length < 2) continue;

    const checkPathStart = _pathStartIndexForFingerNaming(skel, path);
    const firstBone = skel.bone(path[checkPathStart]);
    const nameLower = firstBone.name.toLowerCase();
    const metacarpalRoot =
      checkPathStart === 1 && _isMetacarpalLikeRoot(skel, path[0]) ? path[0] : null;

    for (const [fingerName, patterns] of Object.entries(FINGER_PATTERNS)) {
      if (patterns.some(p => nameLower.includes(p))) {
        const baseName = `${side}${fingerName}`;
        if (baseName in fingers) break;
        if (metacarpalRoot !== null && fingerName !== 'Thumb') {
          fingers[`${baseName}Metacarpal`] = [metacarpalRoot];
        }
        const chain = _chainFromPath(skel, path.slice(checkPathStart));
        fingers[baseName] = chain;
        const matchedPat = patterns.find(p => nameLower.includes(p))!;
        fingerEvidence[baseName] = `Fingers: naming match (${matchedPat})`;
        break;
      }
    }
  }

  // PHASE 2: "FingerN" DIGIT PATTERN
  let nonMetacarpalCount = Object.keys(fingers).filter(k => !k.endsWith('Metacarpal')).length;
  if (nonMetacarpalCount === 0) {
    const digitMatches: [number[], number, number][] = [];
    const phase2Added: string[] = [];

    for (const path of fingerPaths) {
      if (path.length < 2) continue;

      const checkPathStart = _pathStartIndexForFingerNaming(skel, path);
      const checkName = skel.bone(path[checkPathStart]).name.toLowerCase();

      const m = _RE_FINGER_DIGIT.exec(checkName);
      if (m) {
        const digit = parseInt(m[1], 10);
        digitMatches.push([path, checkPathStart, digit]);
      }
    }

    if (digitMatches.length > 0) {
      const digitsPresent = new Set(digitMatches.map(([, , d]) => d));
      let digitMap = FINGER_DIGIT_MAP_0_BASED;
      if (!digitsPresent.has(0) && digitsPresent.has(5)) {
        digitMap = FINGER_DIGIT_MAP_1_BASED;
      }

      for (const [path, checkPathStart, digit] of digitMatches) {
        const fingerName = digitMap[digit];
        if (!fingerName) continue;
        const baseName = `${side}${fingerName}`;
        if (baseName in fingers) continue;
        const chain = _chainFromPath(skel, path.slice(checkPathStart));
        if (chain.length >= 2) {
          fingers[baseName] = chain;
          phase2Added.push(baseName);
          const mode = digitMap === FINGER_DIGIT_MAP_0_BASED ? '0-based' : '1-based';
          fingerEvidence[baseName] = `Fingers: digit pattern (${mode}, Finger${digit} = ${fingerName})`;
        }
      }

      const digitCount = Object.keys(fingers).filter(k => !k.endsWith('Metacarpal')).length;
      if (digitMatches.length >= 4 && digitCount <= 2 && phase2Added.length > 0) {
        for (const chainName of phase2Added) {
          delete fingers[chainName];
          delete fingerEvidence[chainName];
        }
      }
    }
  }

  // PHASE 3: "finger.X" LETTER PATTERN
  nonMetacarpalCount = Object.keys(fingers).filter(k => !k.endsWith('Metacarpal')).length;
  if (nonMetacarpalCount === 0) {
    const letterRoots: [number, number, number[]][] = [];

    for (const path of fingerPaths) {
      if (path.length < 2) continue;

      const checkPathStart = _pathStartIndexForFingerNaming(skel, path);
      const checkName = skel.bone(path[checkPathStart]).name.toLowerCase();

      const m = _RE_FINGER_LETTER.exec(checkName);
      if (m) {
        const letter = m[1].toLowerCase();
        const ordinal = FINGER_LETTER_MAP[letter];
        if (ordinal !== undefined) {
          const chain = _chainFromPath(skel, path.slice(checkPathStart));
          if (chain.length >= 2) {
            letterRoots.push([ordinal, path[checkPathStart], chain]);
          }
        }
      }
    }

    if (letterRoots.length > 0) {
      letterRoots.sort((a, b) => a[0] - b[0]);

      const handE = hand.restWorld.elements;
      const handPos = new Vector3(handE[12], handE[13], handE[14]);
      const rootIdxs = letterRoots.map(([, r]) => r);
      const thumbRoot = _identifyThumbSpatially(skel, rootIdxs, handPos);

      if (thumbRoot !== null) {
        for (const [ordinal, root, chain] of letterRoots) {
          if (root === thumbRoot) {
            const baseName = `${side}Thumb`;
            fingers[baseName] = chain;
            const letterChar = String.fromCharCode('a'.charCodeAt(0) + ordinal);
            fingerEvidence[baseName] = `Fingers: letter pattern (finger.${letterChar} = Thumb, spatial)`;
            break;
          }
        }

        const remaining = letterRoots.filter(([, r]) => r !== thumbRoot);
        const nonThumbNames = nonThumbFingerNamesForAvailableCount(remaining.length);
        for (let i = 0; i < remaining.length; i++) {
          const [ordinal, , chain] = remaining[i];
          const fname = nonThumbNames[i] ?? `Finger${i + 1}`;
          const baseName = `${side}${fname}`;
          if (!(baseName in fingers)) {
            fingers[baseName] = chain;
            const letterChar = String.fromCharCode('a'.charCodeAt(0) + ordinal);
            fingerEvidence[baseName] = `Fingers: letter pattern (finger.${letterChar} = ${fname})`;
          }
        }
      } else {
        for (let i = 0; i < letterRoots.length; i++) {
          const [ordinal, , chain] = letterRoots[i];
          const nonThumbNames = nonThumbFingerNamesForAvailableCount(letterRoots.length);
          const fname = nonThumbNames[i] ?? `Finger${i}`;
          const baseName = `${side}${fname}`;
          if (!(baseName in fingers)) {
            fingers[baseName] = chain;
            const letterChar = String.fromCharCode('a'.charCodeAt(0) + ordinal);
            fingerEvidence[baseName] = `Fingers: letter pattern (finger.${letterChar} = ${fname}, no thumb)`;
          }
        }
      }
    }
  }

  // PHASE 4: GENERIC NUMBERED FALLBACK
  nonMetacarpalCount = Object.keys(fingers).filter(k => !k.endsWith('Metacarpal')).length;
  if (nonMetacarpalCount === 0) {
    const numberedRoots: [number, number][] = [];

    for (const child of handChildren) {
      const nameLower = skel.bone(child).name.toLowerCase();
      if (!GENERIC_FINGER_TOKENS.some((tok) => nameLower.includes(tok))) continue;

      let match = /(?:finger|doigt)[_\-.]?(\d+)/.exec(nameLower);
      if (!match) match = /(\d+)$/.exec(nameLower);
      if (!match) continue;

      numberedRoots.push([child, parseInt(match[1], 10)]);
    }

    if (numberedRoots.length > 0) {
      const rootToChain: Map<number, number[]> = new Map();
      for (const [rootIdx] of numberedRoots) {
        const paths = fingerPaths.filter(p => p.length > 0 && p[0] === rootIdx);
        if (paths.length === 0) continue;
        const bestPath = paths.reduce((a, b) => a.length >= b.length ? a : b);
        rootToChain.set(rootIdx, bestPath);
      }

      const roots = [...rootToChain.keys()];
      if (roots.length > 0) {
        const handE = hand.restWorld.elements;
        const handPos = new Vector3(handE[12], handE[13], handE[14]);

        const thumbRoot = _identifyThumbSpatially(
          skel,
          roots,
          handPos,
          0.3,
          rootToChain,
        );
        if (thumbRoot !== null) {
          const thumbName = `${side}Thumb`;
          if (!(thumbName in fingers) && rootToChain.has(thumbRoot)) {
            fingers[thumbName] = _chainFromGenericPath(
              rootToChain.get(thumbRoot)!,
              true,
            );
            fingerEvidence[thumbName] = `Fingers: numbered pattern (${skel.bone(thumbRoot).name}, spatial thumb)`;
          }
        }

        const remainingRoots = roots.filter(r => r !== thumbRoot);
        if (remainingRoots.length > 0) {
          let thumbPosHint: Vector3 | null = null;
          if (thumbRoot !== null) {
            const te = skel.bone(thumbRoot).restWorld.elements;
            thumbPosHint = new Vector3(te[12], te[13], te[14]);
          }
          const ordered = _orderFingerRootsSpatially(
            skel,
            remainingRoots,
            handIdx,
            side,
            thumbPosHint,
          );
          const nonThumbNames = nonThumbFingerNamesForAvailableCount(ordered.length);
          for (let i = 0; i < ordered.length; i++) {
            const rootIdx = ordered[i];
            const fname = nonThumbNames[i] ?? `Finger${i + 1}`;
            const targetName = `${side}${fname}`;
            if (targetName in fingers) continue;
            if (rootToChain.has(rootIdx)) {
              const path = rootToChain.get(rootIdx)!;
              const splitGenericMetacarpals = thumbRoot !== null && ordered.length < 4;
              const metacarpal = _metacarpalFromGenericPath(
                path,
                false,
                splitGenericMetacarpals,
              );
              if (metacarpal) {
                const metacarpalName = `${targetName}Metacarpal`;
                fingers[metacarpalName] = metacarpal;
                fingerEvidence[metacarpalName] =
                  `Fingers: numbered pattern (${skel.bone(rootIdx).name}, generic metacarpal split)`;
              }
              fingers[targetName] = _chainFromGenericPath(
                path,
                false,
                splitGenericMetacarpals,
              );
              fingerEvidence[targetName] = `Fingers: numbered pattern (${skel.bone(rootIdx).name}, spatial order)`;
            }
          }
        }
      }
    }
  }

  // PHASE 5: GEOMETRY FALLBACK
  nonMetacarpalCount = Object.keys(fingers).filter(k => !k.endsWith('Metacarpal')).length;
  if (nonMetacarpalCount < 2) {
    const handE = hand.restWorld.elements;
    const handPos = new Vector3(handE[12], handE[13], handE[14]);

    const used = new Set<number>();
    for (const chain of Object.values(fingers)) {
      for (const idx of chain) used.add(idx);
    }

    const remainingChildren: number[] = [];
    for (const child of handChildren) {
      if (used.has(child)) continue;
      const nameLower = skel.bone(child).name.toLowerCase();
      if (isMetacarpalLikeName(nameLower)) continue;
      const childPaths = fingerPaths.filter(p => p.length > 0 && p[0] === child && p.length >= 2);
      if (childPaths.length === 0) continue;
      remainingChildren.push(child);
    }

    if (remainingChildren.length > 0) {
      const childToChain: Map<number, number[]> = new Map();
      for (const child of remainingChildren) {
        const childPaths = fingerPaths.filter(p => p.length > 0 && p[0] === child);
        if (childPaths.length > 0) {
          const bestPath = childPaths.reduce((a, b) => a.length >= b.length ? a : b);
          childToChain.set(child, bestPath);
        }
      }

      let thumbRoot: number | null = null;
      if (!(`${side}Thumb` in fingers)) {
        thumbRoot = _identifyThumbSpatially(
          skel,
          remainingChildren,
          handPos,
          0.3,
          childToChain,
        );
      }

      if (thumbRoot !== null && childToChain.has(thumbRoot)) {
        fingers[`${side}Thumb`] = _chainFromGenericPath(
          childToChain.get(thumbRoot)!,
          true,
        );
        fingerEvidence[`${side}Thumb`] = 'Fingers: spatial/geometry analysis (thumb by direction deviation)';
        const updatedRemaining = remainingChildren.filter(c => c !== thumbRoot);
        remainingChildren.length = 0;
        remainingChildren.push(...updatedRemaining);
      }

      if (remainingChildren.length > 0) {
        let thumbPosHint: Vector3 | null = null;
        if (thumbRoot !== null) {
          const te = skel.bone(thumbRoot).restWorld.elements;
          thumbPosHint = new Vector3(te[12], te[13], te[14]);
        }
        const ordered = _orderFingerRootsSpatially(
          skel, remainingChildren, handIdx, side, thumbPosHint,
        );
        const nonThumbNames = nonThumbFingerNamesForAvailableCount(ordered.length);
        for (let i = 0; i < ordered.length; i++) {
          const rootIdx = ordered[i];
          const fname = nonThumbNames[i] ?? `Finger${i + 1}`;
          const targetName = `${side}${fname}`;
          if (targetName in fingers) continue;
          if (childToChain.has(rootIdx)) {
            const path = childToChain.get(rootIdx)!;
            const splitGenericMetacarpals = thumbRoot !== null && ordered.length < 4;
            const metacarpal = _metacarpalFromGenericPath(
              path,
              false,
              splitGenericMetacarpals,
            );
            if (metacarpal) {
              const metacarpalName = `${targetName}Metacarpal`;
              fingers[metacarpalName] = metacarpal;
              fingerEvidence[metacarpalName] =
                'Fingers: spatial/geometry analysis (generic metacarpal split)';
            }
            fingers[targetName] = _chainFromGenericPath(
              path,
              false,
              splitGenericMetacarpals,
            );
            fingerEvidence[targetName] = 'Fingers: spatial/geometry analysis (spatial ordering)';
          }
        }
      }
    }
  }

  return [fingers, fingerEvidence];
}

export function hasNamedFingerDescendants(
  skel: Skeleton,
  handIdx: number,
  minNamedPaths = 2,
): boolean {
  let namedPathCount = 0;

  for (const childIdx of effectiveFingerChildren(skel, handIdx)) {
    const childPaths = extractPathsToLeaves(skel, childIdx, true);
    const hasNamedPath = childPaths.some(
      (path) => path.length >= 2 && _pathHasFingerNaming(skel, path),
    );
    if (!hasNamedPath) continue;

    namedPathCount += 1;
    if (namedPathCount >= minNamedPaths) return true;
  }

  return false;
}
